import React, { useState, useEffect } from 'react';
import { Play, Book, Heart, Users, Globe, ArrowRight, Mail, Music, BookOpen, MapPin, ListMusic, Calendar, Key as KeyIcon, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import subscriptionApi from '../services/subscriptionApi';
import ServiceTimes from '../components/ServiceTimes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const HomePage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [latestPlaylist, setLatestPlaylist] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/playlists`)
      .then(r => r.json())
      .then(playlists => {
        if (playlists.length > 0) {
          // Get the first playlist (latest by date)
          fetch(`${BACKEND_URL}/api/playlists/${playlists[0].id}`)
            .then(r => r.json())
            .then(setLatestPlaylist)
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, []);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [error, setError] = useState('');

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await subscriptionApi.subscribe(email, '', 'general', ['ministry_updates', 'spiritual_insights']);
      setIsSubscribed(true);
      setEmail('');
      
      // Reset after 4 seconds
      setTimeout(() => {
        setIsSubscribed(false);
      }, 4000);
    } catch (err) {
      setError('Please try again');
      console.error('Homepage subscription error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const coreMinistries = [
    {
      title: 'Online Sermons',
      description: 'Powerful biblical messages delivered with clarity and passion to strengthen your faith journey',
      icon: Play,
      href: '/sermons',
      image: 'https://images.unsplash.com/photo-1507692049790-de58290a4334?q=80&w=400&h=250&auto=format&fit=crop'
    },
    {
      title: 'Bible Studies',
      description: 'Deep exploration of Scripture through verse-by-verse teaching and practical application',
      icon: Book,
      href: '/bible-studies',
      image: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?q=80&w=400&h=250&auto=format&fit=crop'
    },
    {
      title: 'Prayer Support',
      description: 'Confidential prayer requests and dedicated intercession for your spiritual needs',
      icon: Heart,
      href: '/prayer',
      image: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?q=80&w=400&h=250&auto=format&fit=crop'
    },
    {
      title: 'Gospel Outreach',
      description: 'Sharing the good news of Jesus Christ through digital evangelism and personal witness',
      icon: Globe,
      href: '/outreach',
      image: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?q=80&w=400&h=250&auto=format&fit=crop'
    }
  ];

  return (
    <div className="pt-16 lg:pt-20">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1496185524395-81f295f4859e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxjaHVyY2glMjB3b3JzaGlwfGVufDB8fHx8MTc1ODU3MzExNnww&ixlib=rb-4.1.0&q=85"
            alt="El Bethel Ministries - Worship Community"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        </div>

        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <div className="mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_f1a5c9b5-8f1b-4d34-88be-faeda192a0ed/artifacts/8ox9chkc_2.png"
              alt="El Bethel Church"
              className="w-20 h-20 lg:w-24 lg:h-24 mx-auto mb-6 drop-shadow-lg"
            />
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight font-display">
              EL BETHEL CHURCH
            </h1>
            <p className="text-lg md:text-xl lg:text-2xl mb-8 font-light leading-relaxed font-sans">
              Exalting Christ Through Expository Preaching and Authentic Worship
            </p>
            <p className="text-base md:text-lg text-white/90 mb-8 font-sans">
              Join us Sundays at 5:00 PM in Grand Prairie, Texas
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/visit"
              className="bg-[#f17b21] hover:bg-[#d86919] text-white px-6 lg:px-8 py-3 lg:py-4 rounded-lg text-base lg:text-lg font-semibold font-sans transition-all duration-300 transform hover:scale-105 inline-block text-center"
            >
              Plan Your Visit
            </a>
            <button 
              onClick={() => navigate('/contact')}
              className="border-2 border-white text-white hover:bg-white hover:text-gray-800 px-6 lg:px-8 py-3 lg:py-4 rounded-lg text-base lg:text-lg font-semibold font-sans transition-all duration-300"
            >
              Contact Us
            </button>
          </div>
        </div>
      </section>

      {/* Service Times */}
      <ServiceTimes />

      {/* This Sunday's Setlist */}
      {latestPlaylist && latestPlaylist.songs && latestPlaylist.songs.length > 0 && (
        <section className="py-12 lg:py-16 bg-stone-900 text-white" data-testid="sunday-setlist-section">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <ListMusic className="w-4 h-4 text-amber-400" />
                    <span className="text-amber-400 text-xs font-semibold tracking-widest uppercase">Worship Setlist</span>
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold font-display">
                    {latestPlaylist.name}
                  </h2>
                  {latestPlaylist.service_date && (
                    <p className="text-stone-400 text-sm mt-1 flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" />
                      {new Date(latestPlaylist.service_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                    </p>
                  )}
                </div>
                <button
                  data-testid="view-full-setlist"
                  onClick={() => navigate(`/playlists/${latestPlaylist.id}`)}
                  className="hidden sm:flex items-center gap-1.5 bg-amber-600 hover:bg-amber-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-all"
                >
                  View Full Setlist <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {latestPlaylist.songs.map((song, idx) => (
                  <button
                    key={song.id}
                    data-testid={`setlist-song-${idx}`}
                    onClick={() => navigate(`/songs/english/${song.id}`)}
                    className="flex items-center gap-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl p-3.5 text-left transition-all group"
                  >
                    <span className="flex-shrink-0 w-9 h-9 bg-amber-600/20 text-amber-400 rounded-lg flex items-center justify-center font-bold text-sm">
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-white truncate text-sm group-hover:text-amber-300 transition-colors">
                        {song.title}
                      </h4>
                      <div className="flex items-center gap-2 mt-0.5">
                        {song.author && <span className="text-xs text-stone-400 truncate max-w-[140px]">{song.author}</span>}
                        {song.key_signature && (
                          <span className="text-xs text-indigo-400 bg-indigo-400/10 px-1.5 py-0.5 rounded font-medium flex items-center gap-0.5">
                            <KeyIcon className="w-2.5 h-2.5" /> {song.key_signature}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              <button
                data-testid="view-full-setlist-mobile"
                onClick={() => navigate(`/playlists/${latestPlaylist.id}`)}
                className="sm:hidden mt-4 w-full flex items-center justify-center gap-1.5 bg-amber-600 hover:bg-amber-700 text-white px-5 py-3 rounded-lg text-sm font-semibold transition-all"
              >
                View Full Setlist <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Start Here Section */}
      <section className="py-14 lg:py-20 bg-gray-50" data-testid="start-here-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10 lg:mb-14">
            <span className="inline-block bg-[#f17b21]/10 text-[#f17b21] text-sm font-semibold px-4 py-1.5 rounded-full mb-4 font-sans">
              START HERE
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4 font-display">
              Not Sure Where to Begin?
            </h2>
            <p className="text-base lg:text-lg text-gray-600 max-w-2xl mx-auto font-sans">
              Whether you're visiting for the first time or exploring our ministry online, here are the best ways to get started.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
            {[
              { icon: MapPin, title: 'Plan Your Visit', desc: 'Service times, directions, and what to expect on Sunday', href: '/visit', color: 'bg-orange-100 text-[#f17b21]' },
              { icon: Play, title: 'Watch a Sermon', desc: 'Start with our latest message — stream anytime, anywhere', href: '/sermons', color: 'bg-blue-100 text-blue-600' },
              { icon: BookOpen, title: 'Daily Devotion', desc: 'A daily word of encouragement from Scripture for your walk', href: '/devotions', color: 'bg-emerald-100 text-emerald-600' },
              { icon: Music, title: 'Song Library', desc: 'Browse 900+ hymns and worship songs with chords and lyrics', href: '/songs/English', color: 'bg-purple-100 text-purple-600' },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.href}
                  onClick={() => navigate(item.href)}
                  data-testid={`start-here-${item.href.replace(/\//g, '-').slice(1)}`}
                  className="group bg-white rounded-xl shadow-md hover:shadow-xl p-6 cursor-pointer transition-all duration-300 hover:-translate-y-1"
                >
                  <div className={`w-12 h-12 ${item.color} rounded-xl flex items-center justify-center mb-4`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-bold text-gray-800 mb-2 group-hover:text-[#f17b21] transition-colors font-display text-base">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-3 font-sans leading-relaxed">{item.desc}</p>
                  <span className="text-[#f17b21] font-semibold text-sm inline-flex items-center gap-1 font-sans">
                    Get Started <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Core Ministries */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 lg:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-6 font-display">
              Our Core Ministries
            </h2>
            <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto font-sans">
              Discover how El Bethel Church is transforming lives through biblical teaching, 
              prayer support, and community outreach rooted in God's unchanging Word.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {coreMinistries.map((ministry, index) => {
              const IconComponent = ministry.icon;
              return (
                <div 
                  key={index} 
                  className="group bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden cursor-pointer"
                  onClick={() => navigate(ministry.href)}
                >
                  <div className="relative h-40 lg:h-48 overflow-hidden">
                    <img 
                      src={ministry.image}
                      alt={ministry.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="w-12 h-12 bg-[#f17b21] rounded-full flex items-center justify-center">
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-lg lg:text-xl font-bold text-gray-800 mb-3 group-hover:text-[#f17b21] transition-colors duration-300 font-display">
                      {ministry.title}
                    </h3>
                    <p className="text-gray-600 mb-4 leading-relaxed font-sans text-sm lg:text-base">
                      {ministry.description}
                    </p>
                    <div className="flex items-center text-[#f17b21] hover:text-[#d86919] font-semibold text-sm transition-colors duration-300 font-sans">
                      <span>Explore</span>
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 lg:py-20 bg-gradient-to-br from-[#f17b21] to-[#ff9649]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 font-display">
            Stay Connected with Our Ministry
          </h2>
          <p className="text-lg lg:text-xl text-white/90 mb-8 max-w-3xl mx-auto font-sans">
            Join our ministry community and receive weekly spiritual insights, ministry updates, 
            and biblical encouragement directly in your inbox.
          </p>
          
          {/* Subscription Form */}
          <div className="max-w-lg mx-auto bg-white/10 backdrop-blur-sm rounded-2xl p-6 lg:p-8 mb-8">
            {!isSubscribed ? (
              <form onSubmit={handleSubscribe}>
                <div className="flex flex-col sm:flex-row gap-4">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="flex-1 px-4 py-3 lg:py-4 rounded-lg text-gray-800 placeholder-gray-500 focus:ring-2 focus:ring-white focus:outline-none font-sans"
                    required
                  />
                  <button 
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-white text-[#f17b21] hover:bg-gray-100 px-6 lg:px-8 py-3 lg:py-4 rounded-lg font-semibold transition-colors duration-300 font-sans flex items-center justify-center space-x-2 whitespace-nowrap disabled:opacity-70"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin w-4 h-4 border-2 border-[#f17b21] border-t-transparent rounded-full"></div>
                        <span>Subscribing...</span>
                      </>
                    ) : (
                      <>
                        <Mail className="w-5 h-5" />
                        <span>Subscribe Free</span>
                      </>
                    )}
                  </button>
                </div>
                {error && (
                  <p className="text-white/80 text-sm mt-2 text-center font-sans">{error}</p>
                )}
              </form>
            ) : (
              <div className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-8 h-8 text-white" />
                </div>
                <h4 className="text-xl font-bold text-white mb-2 font-display">Welcome to the Family!</h4>
                <p className="text-white/90 font-sans">Thank you for subscribing! You'll receive spiritual encouragement soon.</p>
              </div>
            )}
            <p className="text-white/80 text-sm mt-3 font-sans text-center">
              ✨ Weekly spiritual insights • 🙏 Prayer encouragement • 📖 Ministry updates • No spam, ever
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/sermons')}
              className="bg-white text-[#f17b21] hover:bg-gray-100 px-6 lg:px-8 py-3 lg:py-4 rounded-lg font-semibold transition-colors duration-300 font-sans"
            >
              Start Watching Sermons
            </button>
            <button 
              onClick={() => navigate('/bible-studies')}
              className="border-2 border-white text-white hover:bg-white hover:text-[#f17b21] px-6 lg:px-8 py-3 lg:py-4 rounded-lg font-semibold transition-colors duration-300 font-sans"
            >
              Join Bible Study
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;